/**
 * Chilean Public Holidays
 * 
 * This file contains the list of Chilean public holidays (feriados).
 * Includes both fixed holidays and movable holidays (Easter-based).
 * 
 * Update annually to add new years or changes to holiday calendar.
 */

/**
 * Calculate Easter Sunday for a given year using Meeus/Jones/Butcher algorithm
 * @param {number} year - The year to calculate Easter for
 * @returns {Date} Easter Sunday date
 */
export const calculateEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
};

/**
 * Fixed Chilean holidays (same date every year)
 */
export const FIXED_HOLIDAYS = [
  { month: 1, day: 1, name: 'Año Nuevo' },
  { month: 5, day: 1, name: 'Día del Trabajo' },
  { month: 5, day: 21, name: 'Día de las Glorias Navales' },
  { month: 6, day: 29, name: 'San Pedro y San Pablo' },
  { month: 7, day: 16, name: 'Día de la Virgen del Carmen' },
  { month: 8, day: 15, name: 'Asunción de la Virgen' },
  { month: 9, day: 18, name: 'Primera Junta Nacional de Gobierno' },
  { month: 9, day: 19, name: 'Día de las Glorias del Ejército' },
  { month: 10, day: 12, name: 'Encuentro de Dos Mundos' },
  { month: 10, day: 31, name: 'Día Nacional de las Iglesias Evangélicas y Protestantes' },
  { month: 11, day: 1, name: 'Día de Todos los Santos' },
  { month: 12, day: 8, name: 'Inmaculada Concepción' },
  { month: 12, day: 25, name: 'Navidad' },
];

/**
 * Get all Chilean holidays for a given year
 * Includes both fixed and movable (Easter-based) holidays
 * 
 * @param {number} year - The year to get holidays for
 * @returns {Array<{date: Date, name: string, movable: boolean}>} Array of holiday objects
 */
export const getChileanHolidays = (year) => {
  const holidays = [];
  
  // Add fixed holidays
  FIXED_HOLIDAYS.forEach(holiday => {
    holidays.push({
      date: new Date(year, holiday.month - 1, holiday.day),
      name: holiday.name,
      movable: false
    });
  });
  
  // Add movable holidays (Easter-based)
  const easter = calculateEaster(year);
  
  // Viernes Santo (Good Friday) - 2 days before Easter
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  holidays.push({
    date: goodFriday,
    name: 'Viernes Santo',
    movable: true
  });
  
  // Sábado Santo (Holy Saturday) - 1 day before Easter
  const holySaturday = new Date(easter);
  holySaturday.setDate(easter.getDate() - 1);
  holidays.push({
    date: holySaturday,
    name: 'Sábado Santo',
    movable: true
  });
  
  return holidays;
};

/**
 * Check if a given date is a Chilean public holiday
 * 
 * @param {Date} date - The date to check
 * @returns {boolean} True if the date is a holiday, false otherwise
 */
export const isChileanHoliday = (date) => {
  const year = date.getFullYear();
  const holidays = getChileanHolidays(year);
  
  return holidays.some(holiday => {
    return holiday.date.getFullYear() === date.getFullYear() &&
           holiday.date.getMonth() === date.getMonth() &&
           holiday.date.getDate() === date.getDate();
  });
};
